/**
 * Apply Brand CSS Variables
 * Called once at app startup to inject brand colors into :root.
 * This overrides the defaults in global.css at runtime,
 * keeping global.css untouched for clean merges.
 */

import type { BrandConfig } from './types';
import { getActiveOverrides } from './index';

function hexToRgbTriplet(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r}, ${g}, ${b}`;
}

function hexToRgba(hex: string, alpha: number): string {
  return `rgba(${hexToRgbTriplet(hex)}, ${alpha})`;
}

/**
 * 1×1 transparent PNG. Used as the favicon when the active tenant
 * has no custom icon — guarantees we never leave another tenant's
 * icon (which may be sitting in the static `/favicon-32.png` slot,
 * or which was applied by a previous brand on this tab) showing in
 * the browser tab. A blank icon is the lesser evil compared to
 * "user sees competitor M²'s logo on the CorpBrain admin tab".
 */
const BLANK_FAVICON =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNgAAIAAAUAAen63NgAAAAASUVORK5CYII=';

/**
 * Swap the browser-tab favicon. ALWAYS sets a value — either the
 * tenant's custom icon, or the blank pixel — so no tenant ever
 * inherits the static deploy's default favicon (which historically
 * was a specific tenant's mark and bled across the platform).
 */
function applyBrandFavicon(brand: BrandConfig): void {
  // Resolution order: dedicated favicon, then dark/light icon as a fallback
  // (legacy behavior so tenants who only uploaded an icon keep their
  // browser-tab mark), then the blank pixel. Favicons don't have a
  // dark-mode variant by design — tab chrome already adapts.
  const dark = document.documentElement.getAttribute('data-theme') === 'dark';
  const icon =
    brand.favicon ||
    (dark && brand.iconDark) ||
    brand.icon ||
    BLANK_FAVICON;
  const links = document.querySelectorAll('link[rel~="icon"], link[rel="apple-touch-icon"]');
  links.forEach((node) => {
    (node as HTMLLinkElement).href = icon;
  });
  // Cache so the pre-paint inline script in index.html can apply the
  // right favicon BEFORE React mounts on the next refresh — same
  // model as the pageTitle cache. We cache even the BLANK_FAVICON
  // so refresh on a no-icon tenant doesn't briefly show whatever
  // was in the static file.
  try {
    const slug = window.location.pathname.match(/^\/([^/]+)/)?.[1]?.toLowerCase() || '';
    const key = slug
      ? 'brand_favicon:tenant:' + slug
      : 'brand_favicon:host:' + window.location.host;
    window.localStorage.setItem(key, icon);
  } catch { /* private mode etc — non-fatal */ }
}

export function applyBrandCSS(brand: BrandConfig): void {
  const root = document.documentElement;

  // Browser-tab favicon — keep in sync with the active tenant brand.
  applyBrandFavicon(brand);

  // Per-agent colored halo on the home carousel is part of the
  // M² "tint by agent" treatment. Gate it on the SAME explicit
  // `chat.tintByAgent` flag the chat surface already uses — that
  // flag is set in M²'s brand_overrides and unset on tenants that
  // didn't opt into the multi-color agent theme (e.g. corpbrain-admin,
  // whose `agentColors` happens to be the M² palette copy-paste but
  // whose `chat` field is empty). Reusing the existing flag avoids
  // a new field and a data migration: M² renders unchanged, and any
  // tenant without `chat.tintByAgent=true` doesn't get the halo.
  const overrides = getActiveOverrides();
  const tintByAgent = !!overrides?.chat?.tintByAgent;
  if (tintByAgent) {
    root.style.setProperty('--show-agent-halo', '1');
  } else {
    root.style.removeProperty('--show-agent-halo');
  }

  // Brand colors
  root.style.setProperty('--primary', brand.colors.primary);
  root.style.setProperty('--primary-hover', brand.colors.primaryHover);
  root.style.setProperty('--secondary', brand.colors.secondary);
  root.style.setProperty('--secondary-light', brand.colors.secondaryLight);
  root.style.setProperty('--secondary-dark', brand.colors.secondaryDark);
  root.style.setProperty('--accent', brand.colors.accent);
  root.style.setProperty('--gradient-primary', brand.colors.gradientPrimary);

  // Alpha variants computed from primary
  root.style.setProperty('--primary-alpha-10', hexToRgba(brand.colors.primary, 0.1));
  root.style.setProperty('--primary-alpha-15', hexToRgba(brand.colors.primary, 0.15));
  root.style.setProperty('--primary-alpha-40', hexToRgba(brand.colors.primary, 0.4));

  // The primary as a bare "r, g, b" triplet, so a stylesheet can mix ANY
  // alpha it needs with `rgba(var(--primary-rgb), 0.18)` instead of either
  // hardcoding the brand hex or forcing a new --primary-alpha-NN token for
  // every opacity a component happens to want. Added because the shared
  // hierarchy tree needed nine different alphas of the brand colour and had
  // been carrying M²'s literal #6CC7EF, which then rendered under every
  // tenant's brand once the super-admin console mounted that tree.
  root.style.setProperty('--primary-rgb', hexToRgbTriplet(brand.colors.primary));

  // Status colors (--success, --warning, --error, --info) are platform-wide
  // CONSTANTS, not per-tenant. They live in `frontend/src/styles/tokens/{raw,semantic}.css`
  // and are deliberately NOT overwritten here — recoloring "error" or "success"
  // per tenant breaks accessibility expectations. `--info` is wired to
  // `--primary` inside semantic.css so info-tinted surfaces inherit the brand.
  // (This block intentionally left non-mutating — see WHITE_LABELING.md.)

  // Global color override — replace old blue (#3b82f6/#2563eb) with M² primary
  // This catches all hardcoded blues across 40+ CSS files without touching them
  const colorOverride = document.createElement('style');
  colorOverride.setAttribute('data-brand', 'color-unify');
  colorOverride.textContent = `
    /* ===== Primary buttons — brand gradient (consistent across pages) =====
       :not(:disabled) is load-bearing. These rules are !important, so without
       it they repainted DISABLED buttons in the full brand gradient with white
       text, overriding both Ant Design's disabled styling and any local rule:
       every disabled primary button in the product looked ready to press.
       Measured on the Library share drawer 2026-08-29, where the one action on
       the panel sat bright and inert until something was selected. */
    .btn-primary:not(:disabled),
    .bots-create-btn:not(:disabled),
    .bots-empty-btn:not(:disabled),
    .create-btn:not(:disabled),
    .skills-create-btn:not(:disabled),
    .ant-btn-primary:not(:disabled) {
      background: ${brand.colors.gradientPrimary} !important;
      border-color: ${brand.colors.primary} !important;
      color: #fff !important;
    }

    /* A disabled primary reads as "not yet", in the brand's own muted tones
       rather than the framework's default grey. */
    .btn-primary:disabled,
    .bots-create-btn:disabled,
    .bots-empty-btn:disabled,
    .create-btn:disabled,
    .skills-create-btn:disabled,
    .ant-btn-primary:disabled {
      background: ${hexToRgba(brand.colors.primary, 0.12)} !important;
      background-image: none !important;
      border-color: ${hexToRgba(brand.colors.primary, 0.22)} !important;
      color: ${hexToRgba(brand.colors.primary, 0.55)} !important;
      box-shadow: none !important;
      cursor: not-allowed !important;
    }

    /* Hover only applies where a press is possible. */
    .btn-primary:hover:not(:disabled),
    .bots-create-btn:hover:not(:disabled),
    .skills-create-btn:hover:not(:disabled),
    .ant-btn-primary:hover:not(:disabled) {
      filter: brightness(0.95) !important;
      border-color: ${brand.colors.primaryHover} !important;
    }

    /* Active nav items — M² primary */
    .nav-item.active {
      background: ${hexToRgba(brand.colors.primary, 0.1)} !important;
      color: ${brand.colors.primary} !important;
    }

    .sidebar .conversation-item.active {
      background: ${hexToRgba(brand.colors.primary, 0.1)} !important;
      color: ${brand.colors.primary} !important;
    }

    /* Links — M² primary */
    a:not(.ant-btn) {
      color: ${brand.colors.primary};
    }

    a:not(.ant-btn):hover {
      color: ${brand.colors.primaryHover};
    }

    /* Focus rings — M² primary, only for Ant Design form components.
       Native inputs/textareas get their own chrome-less focus (set by
       the component CSS) so the design surfaces stay clean. */
    .ant-input:focus,
    .ant-input-focused,
    .ant-input-affix-wrapper-focused,
    .ant-select-focused .ant-select-selector {
      border-color: ${brand.colors.primary} !important;
      box-shadow: 0 0 0 2px ${hexToRgba(brand.colors.primary, 0.15)} !important;
    }

    /* Tabs — active tab M² primary */
    .ant-tabs-tab-active .ant-tabs-tab-btn,
    .tab-btn.active {
      color: ${brand.colors.primary} !important;
      border-bottom-color: ${brand.colors.primary} !important;
    }

    .ant-tabs-ink-bar {
      background: ${brand.colors.primary} !important;
    }

    /* Switches / Toggles */
    .ant-switch-checked {
      background: ${brand.colors.primary} !important;
    }

    /* Checkboxes */
    .ant-checkbox-checked .ant-checkbox-inner {
      background-color: ${brand.colors.primary} !important;
      border-color: ${brand.colors.primary} !important;
    }

    /* Pagination active */
    .ant-pagination-item-active {
      border-color: ${brand.colors.primary} !important;
    }
    .ant-pagination-item-active a {
      color: ${brand.colors.primary} !important;
    }

    /* Spin */
    .ant-spin-dot-item {
      background-color: ${brand.colors.primary} !important;
    }

    /* ===== Replace all purple/violet (#8b5cf6, #7c3aed, #a78bfa, etc.) with M² blue shades ===== */
    /* Brandbook blue row: #E2F0F7 (lightest) → #6CC7EF → #45B1DB → ${brand.colors.primary} (deepest) */

    /* Skills page — pills, badges, icons, buttons.
       Note: .skill-badge is intentionally excluded — that class names the
       in-chat skill pills, which must take the active agent's hue (see
       SkillBadges.css), not the brand primary. */
    .skill-icon,
    .skill-type-badge,
    .skill-status-dot.active {
      background: ${brand.colors.primary} !important;
    }

    .skill-item:hover,
    .skill-item.selected {
      border-color: ${brand.colors.primary} !important;
    }

    .skill-name-link,
    .skill-action-btn:hover {
      color: ${brand.colors.primary} !important;
    }

    /* Data Bundles — purple accents → M² blue */
    .bundle-icon,
    .bundle-badge,
    .bundle-type-badge,
    .sync-status-dot.active {
      background: ${brand.colors.primary} !important;
    }

    .bundle-item:hover,
    .bundle-item.selected {
      border-color: ${brand.colors.primary} !important;
    }

    /* Share modal — purple → M² blue. Only the button: the row badges
       (.share-badge) are tinted labels built on semantic tokens that
       already follow the brand; a solid fill here painted their text
       colours onto solid blue. */
    .share-btn {
      background: ${brand.colors.primary} !important;
      border-color: ${brand.colors.primary} !important;
    }

    .share-btn:hover {
      background: ${brand.colors.primaryHover} !important;
    }

    /* Filter pills / tabs with purple → M² blue */
    .filter-pill.active,
    .tab-pill.active,
    .category-pill.active {
      background: ${brand.colors.primary} !important;
      border-color: ${brand.colors.primary} !important;
      color: #fff !important;
    }

    .filter-pill:hover,
    .tab-pill:hover,
    .category-pill:hover {
      border-color: ${brand.colors.primary} !important;
      color: ${brand.colors.primary} !important;
    }

    /* Bulk action bar */
    .bulk-action-bar,
    .selection-bar {
      background: ${brand.colors.primary} !important;
      border-color: ${brand.colors.primary} !important;
    }

    /* Agent debug panel */
    .debug-tool-icon {
      color: ${brand.colors.primary} !important;
    }

    /* Light mode purple overrides.
       Note: .skill-badge intentionally excluded — see comment on the main
       rule above. In-chat badges must take the agent's hue. */
    [data-theme="light"] .skill-icon,
    [data-theme="light"] .bundle-icon,
    [data-theme="light"] .bundle-badge,
    [data-theme="light"] .filter-pill.active,
    [data-theme="light"] .tab-pill.active,
    [data-theme="light"] .share-btn,
    [data-theme="light"] .bulk-action-bar,
    [data-theme="light"] .selection-bar {
      background: ${brand.colors.primary} !important;
      border-color: ${brand.colors.primary} !important;
    }

    [data-theme="light"] .skill-name-link,
    [data-theme="light"] .skill-action-btn:hover,
    [data-theme="light"] .filter-pill:hover,
    [data-theme="light"] .tab-pill:hover {
      color: ${brand.colors.primary} !important;
    }

    /* ===== GLOBAL purple→blue replacement for Skills, DataBundles, Share, etc. ===== */
    /* Skills page */
    .skills-filter-pill.active {
      background: ${hexToRgba(brand.colors.primary, 0.15)} !important;
      border-color: ${hexToRgba(brand.colors.primary, 0.3)} !important;
      color: ${brand.colors.primary} !important;
    }
    .skills-filter-pill.active:hover {
      background: ${hexToRgba(brand.colors.primary, 0.2)} !important;
    }
    .skills-section-dot.shared { background: ${brand.colors.primary} !important; }

    .skill-card-selected {
      border-color: ${hexToRgba(brand.colors.primary, 0.4)} !important;
      background: ${hexToRgba(brand.colors.primary, 0.05)} !important;
    }
    .skill-card-selected:hover {
      border-color: ${hexToRgba(brand.colors.primary, 0.5)} !important;
      background: ${hexToRgba(brand.colors.primary, 0.08)} !important;
    }

    .skill-card-checkbox-wrapper:hover .skill-card-checkbox-custom {
      border-color: ${brand.colors.primary} !important;
    }
    .skill-card-checkbox:checked + .skill-card-checkbox-custom {
      background: ${brand.colors.primary} !important;
      border-color: ${brand.colors.primary} !important;
    }

    .skill-card-label-agents {
      background: ${hexToRgba(brand.colors.primary, 0.1)} !important;
      color: ${brand.colors.primary} !important;
    }

    .skills-bulk-bar {
      border-color: ${brand.colors.primary} !important;
    }
    .skills-bulk-count {
      color: ${brand.colors.primary} !important;
    }
    .skills-bulk-share {
      background: ${brand.colors.primary} !important;
    }
    .skills-bulk-share:hover {
      background: ${brand.colors.primaryHover} !important;
    }

    /* Skill view modal */
    .svm-accent-bar {
      background: linear-gradient(90deg, ${brand.colors.primary}, ${brand.colors.primaryHover}) !important;
    }
    .svm-chip-shared {
      color: ${brand.colors.primary} !important;
    }
    .svm-spinner {
      border-top-color: ${brand.colors.primary} !important;
    }
    .svm-stat-icon {
      background: ${hexToRgba(brand.colors.primary, 0.12)} !important;
      color: ${brand.colors.primary} !important;
    }

    /* Light mode skills overrides */
    [data-theme="light"] .skill-card-selected {
      border-color: ${hexToRgba(brand.colors.primary, 0.3)} !important;
      background: ${hexToRgba(brand.colors.primary, 0.03)} !important;
    }
    [data-theme="light"] .skill-card-selected:hover {
      background: ${hexToRgba(brand.colors.primary, 0.06)} !important;
    }
    [data-theme="light"] .skill-card-label-agents {
      background: ${hexToRgba(brand.colors.primary, 0.08)} !important;
      color: ${brand.colors.primary} !important;
    }
    [data-theme="light"] .skills-filter-pill.active {
      background: ${hexToRgba(brand.colors.primary, 0.1)} !important;
      border-color: ${hexToRgba(brand.colors.primary, 0.3)} !important;
      color: ${brand.colors.primary} !important;
    }
    [data-theme="light"] .skills-bulk-bar {
      border-color: ${brand.colors.primary} !important;
    }
    [data-theme="light"] .svm-chip-shared {
      color: ${brand.colors.primary} !important;
    }

    /* ===== Data Bundles — all purple → M² blue ===== */
    .bundles-filter-pill--active,
    .bundles-filter-pill--active:hover {
      background: ${hexToRgba(brand.colors.primary, 0.15)} !important;
      border-color: ${hexToRgba(brand.colors.primary, 0.3)} !important;
      color: ${brand.colors.primary} !important;
    }
    .bundle-card-row--selected {
      border-color: ${hexToRgba(brand.colors.primary, 0.4)} !important;
      background: ${hexToRgba(brand.colors.primary, 0.05)} !important;
    }
    .bundle-card-row--selected:hover {
      border-color: ${hexToRgba(brand.colors.primary, 0.5)} !important;
      background: ${hexToRgba(brand.colors.primary, 0.08)} !important;
    }
    .bundle-card-checkbox input:checked + .bundle-card-checkbox-visual {
      background: ${brand.colors.primary} !important;
      border-color: ${brand.colors.primary} !important;
    }
    .bundle-card-checkbox:hover .bundle-card-checkbox-visual {
      border-color: ${brand.colors.primary} !important;
    }
    .bundle-card-shared-badge {
      background: ${hexToRgba(brand.colors.primary, 0.15)} !important;
      color: ${brand.colors.primary} !important;
    }
    .bundle-card-label-sync {
      background: ${hexToRgba(brand.colors.primary, 0.1)} !important;
      color: ${brand.colors.primary} !important;
    }
    .bundles-bulk-share {
      background: ${brand.colors.primary} !important;
      border-color: ${brand.colors.primary} !important;
    }
    .bundles-bulk-share:hover {
      background: ${brand.colors.primaryHover} !important;
      border-color: ${brand.colors.primaryHover} !important;
    }

    /* DataBundleViewModal */
    .bvm-chip-shared {
      background: ${hexToRgba(brand.colors.primary, 0.12)} !important;
      color: ${brand.colors.primary} !important;
      border-color: ${hexToRgba(brand.colors.primary, 0.25)} !important;
    }
    .bvm-source-cloud,
    .bvm-sync-provider {
      background: ${hexToRgba(brand.colors.primary, 0.1)} !important;
      color: ${brand.colors.primary} !important;
      border-color: ${hexToRgba(brand.colors.primary, 0.2)} !important;
    }
    .bvm-sync-icon-wrap {
      background: ${hexToRgba(brand.colors.primary, 0.12)} !important;
      border-color: ${hexToRgba(brand.colors.primary, 0.22)} !important;
      color: ${brand.colors.primary} !important;
    }
    .bvm-file-cloud-badge {
      background: ${hexToRgba(brand.colors.primary, 0.1)} !important;
      color: ${brand.colors.primary} !important;
      border-color: ${hexToRgba(brand.colors.primary, 0.2)} !important;
    }

    /* DataBundleModal — sync folder items */
    .bundle-modal .sync-folder-item:hover {
      border-color: ${hexToRgba(brand.colors.primary, 0.3)} !important;
    }
    .bundle-modal .sync-folder-icon {
      background: ${hexToRgba(brand.colors.primary, 0.1)} !important;
    }
    .bundle-modal .btn-sync {
      background: ${hexToRgba(brand.colors.primary, 0.1)} !important;
      border-color: ${hexToRgba(brand.colors.primary, 0.2)} !important;
      color: ${brand.colors.primary} !important;
    }
    .bundle-modal .btn-sync:hover:not(:disabled) {
      background: ${hexToRgba(brand.colors.primary, 0.15)} !important;
      border-color: ${hexToRgba(brand.colors.primary, 0.3)} !important;
    }

    /* Cloud file picker */
    .cloud-picker-item-icon.folder-icon {
      background: ${hexToRgba(brand.colors.primary, 0.1)} !important;
    }

    /* Light mode data bundles */
    [data-theme="light"] .bundles-filter-pill--active,
    [data-theme="light"] .bundles-filter-pill--active:hover {
      background: ${hexToRgba(brand.colors.primary, 0.1)} !important;
      border-color: ${hexToRgba(brand.colors.primary, 0.3)} !important;
      color: ${brand.colors.primary} !important;
    }
    [data-theme="light"] .bundle-card-row--selected {
      border-color: ${hexToRgba(brand.colors.primary, 0.25)} !important;
      background: ${hexToRgba(brand.colors.primary, 0.03)} !important;
    }
    [data-theme="light"] .bundle-card-label-sync {
      background: ${hexToRgba(brand.colors.primary, 0.08)} !important;
      color: ${brand.colors.primary} !important;
    }
    [data-theme="light"] .bundle-card-shared-badge {
      background: ${hexToRgba(brand.colors.primary, 0.1)} !important;
      color: ${brand.colors.primary} !important;
    }
    [data-theme="light"] .bvm-chip-shared,
    [data-theme="light"] .bvm-source-cloud,
    [data-theme="light"] .bvm-sync-provider {
      color: ${brand.colors.primary} !important;
    }

    /* BotShareModal — purple → M² blue */
    .share-header-icon {
      background: ${hexToRgba(brand.colors.primary, 0.15)} !important;
    }
    .share-type-option.selected .share-type-option-icon {
      background: ${hexToRgba(brand.colors.primary, 0.2)} !important;
    }
    .share-count-badge {
      background: ${hexToRgba(brand.colors.primary, 0.2)} !important;
      color: ${brand.colors.primary} !important;
    }
    .option-avatar.tenant {
      background: linear-gradient(135deg, ${brand.colors.primary} 0%, ${brand.colors.primaryHover} 100%) !important;
    }
    .share-confirm-btn {
      background: ${brand.colors.primary} !important;
      border-color: ${brand.colors.primary} !important;
    }
    .share-confirm-btn:hover {
      background: ${brand.colors.primaryHover} !important;
    }

    /* CSS custom properties used in share modal */
    :root {
      --option-badge-bg: ${hexToRgba(brand.colors.primary, 0.2)} !important;
      --option-badge-color: ${brand.colors.primary} !important;
    }

    /* Progress bar */
    .ant-progress-bg {
      background-color: ${brand.colors.primary} !important;
    }

    /* Admin page — hierarchy tree circles & connectors */
    .tree-connector-dot {
      background: ${brand.colors.primary} !important;
      border-color: ${brand.colors.primary} !important;
    }

    .tree-node-content:hover,
    .tree-node-content.tree-node-selected {
      border-color: ${brand.colors.primary} !important;
    }

    /* Admin light mode — badges, tabs, buttons, borders. Enabled buttons
       only: this rule outranks the muted disabled style above, and it used
       to paint a disabled button's background in the colour of its text,
       leaving a blank blue button ("Save connector", seen live). */
    [data-theme="light"] .btn-primary:not(:disabled),
    [data-theme="light"] .btn-sm.btn-primary:not(:disabled) {
      background: ${brand.colors.primary} !important;
      border-color: ${brand.colors.primary} !important;
    }

    [data-theme="light"] .btn-primary:hover:not(:disabled) {
      background: ${brand.colors.primaryHover} !important;
      border-color: ${brand.colors.primaryHover} !important;
    }

    [data-theme="light"] .tab-btn.active {
      color: ${brand.colors.primary} !important;
      border-bottom-color: ${brand.colors.primary} !important;
    }

    [data-theme="light"] .card:hover,
    [data-theme="light"] .action-card:hover {
      border-color: ${brand.colors.primary} !important;
    }

    [data-theme="light"] .tree-connector-dot {
      background: ${brand.colors.primary} !important;
      border-color: ${brand.colors.primary} !important;
    }

    [data-theme="light"] .badge-count {
      background: ${brand.colors.primary} !important;
    }

    /* Bot item hover */
    .bot-item:hover {
      border-color: ${hexToRgba(brand.colors.primary, 0.5)} !important;
      box-shadow: 0 4px 12px ${hexToRgba(brand.colors.primary, 0.15)} !important;
    }

    /* Chat input send button */
    .send-button:not(:disabled) {
      background: ${brand.colors.primary} !important;
      color: #fff !important;
    }

    .send-button:not(:disabled):hover {
      background: ${brand.colors.primaryHover} !important;
    }
  `;
  document.querySelector('style[data-brand="color-unify"]')?.remove();
  document.head.appendChild(colorOverride);

  // Dark mode overrides — backgrounds, text, borders
  if (brand.darkMode) {
    const style = document.createElement('style');
    style.setAttribute('data-brand', 'dark-overrides');
    style.textContent = `:root {
      --bg-primary: ${brand.darkMode.bgPrimary};
      --bg-secondary: ${brand.darkMode.bgSecondary};
      --bg-tertiary: ${brand.darkMode.bgTertiary};
      --bg-elevated: ${brand.darkMode.bgElevated};

      --text-primary: #eaf2f8;
      --text-secondary: #b8cdd9;
      --text-tertiary: #7a95a8;
      --text-muted: #506878;

      --border: #1e3040;
      --border-light: #2a3e50;
      --border-color: #1e3040;
      --border-hover: #3a5568;
      --border-subtle: rgba(108, 199, 239, 0.08);

      --shadow-card: 0 4px 8px -2px rgba(8, 20, 32, 0.25);
      --shadow-card-hover: 0 8px 16px -4px rgba(8, 20, 32, 0.3);
      --shadow-glow: 0 0 20px ${hexToRgba(brand.colors.primary, 0.12)};
    }`;
    document.querySelector('style[data-brand="dark-overrides"]')?.remove();
    document.head.appendChild(style);
  }

  // Light mode overrides
  if (brand.lightMode) {
    const style = document.createElement('style');
    style.setAttribute('data-brand', 'light-overrides');
    style.textContent = `[data-theme="light"] {
      --bg-primary: ${brand.lightMode.bgPrimary};
      --bg-secondary: ${brand.lightMode.bgSecondary};
      --bg-tertiary: ${brand.lightMode.bgTertiary};
      --bg-elevated: ${brand.lightMode.bgElevated};

      --text-primary: #0f2a3d;
      --text-secondary: #2e4a5c;
      --text-tertiary: #5e7d8f;
      --text-muted: #8ea8b8;

      --border: #c8dce8;
      --border-light: #dbe9f0;
      --border-color: #c8dce8;
      --border-hover: #a0c4d8;
      --border-subtle: rgba(74, 171, 206, 0.1);

      /* Status colors are platform constants — see tokens/semantic.css.
         Light-mode tweaks for those live in tokens/semantic.css's
         [data-theme="light"] block, not here. */

      --shadow-card: 0 4px 8px -2px rgba(15, 42, 61, 0.06);
      --shadow-card-hover: 0 8px 16px -4px rgba(15, 42, 61, 0.1);
      --shadow-glow: 0 0 20px ${hexToRgba(brand.colors.primary, 0.08)};
    }`;
    document.querySelector('style[data-brand="light-overrides"]')?.remove();
    document.head.appendChild(style);
  }

  // Dark mode: force-fix hardcoded backgrounds in component CSS files.
  // Uses html:not([data-theme="light"]) since dark mode has no data-theme attribute.
  if (brand.darkMode) {
    const d = brand.darkMode;
    const darkFix = document.createElement('style');
    darkFix.setAttribute('data-brand', 'dark-mode-fixes');
    darkFix.textContent = `
      html:not([data-theme="light"]) {

        /* ===== Ant Design component overrides ===== */
        .ant-modal-content,
        .ant-modal-header,
        .ant-modal-body,
        .ant-drawer-content,
        .ant-drawer-header,
        .ant-drawer-body,
        .ant-popover-inner,
        .ant-dropdown-menu,
        .ant-select-dropdown,
        .ant-picker-panel-container,
        .ant-collapse-content,
        .ant-tabs-content,
        .ant-card,
        .ant-card-body,
        .ant-descriptions-view,
        .ant-form-item-control-input,
        .ant-notification-notice,
        .ant-list-item {
          background: ${d.bgSecondary} !important;
          color: #eaf2f8 !important;
        }

        .ant-table,
        .ant-table-container,
        .ant-table-tbody > tr > td,
        .ant-table-placeholder {
          background: ${d.bgSecondary} !important;
          color: #eaf2f8 !important;
        }

        .ant-table-thead > tr > th {
          background: ${d.bgTertiary} !important;
          color: #b8cdd9 !important;
          border-bottom: 1px solid #1e3040 !important;
        }

        .ant-table-tbody > tr:hover > td {
          background: ${d.bgTertiary} !important;
        }

        .ant-table-tbody > tr > td {
          border-bottom: 1px solid #1e3040 !important;
        }

        .ant-select-selector,
        .ant-input,
        .ant-input-affix-wrapper,
        .ant-input-number,
        .ant-picker {
          background: ${d.bgTertiary} !important;
          border-color: #1e3040 !important;
          color: #eaf2f8 !important;
        }

        .ant-select-selection-item,
        .ant-select-item-option-content {
          color: #eaf2f8 !important;
        }

        .ant-select-item-option-active,
        .ant-select-item-option-selected {
          background: ${d.bgElevated} !important;
        }

        .ant-tabs-tab {
          color: #7a95a8 !important;
        }
        .ant-tabs-tab-active .ant-tabs-tab-btn {
          color: ${brand.colors.primary} !important;
        }

        .ant-tag {
          background: ${d.bgTertiary} !important;
          border-color: #1e3040 !important;
          color: #b8cdd9 !important;
        }

        .ant-badge-count {
          background: ${brand.colors.primary} !important;
        }

        .ant-empty-description {
          color: #506878 !important;
        }

        .ant-pagination-item,
        .ant-pagination-prev .ant-pagination-item-link,
        .ant-pagination-next .ant-pagination-item-link {
          background: ${d.bgTertiary} !important;
          border-color: #1e3040 !important;
          color: #b8cdd9 !important;
        }

        /* ===== Empty states & content areas ===== */
        .empty-state,
        .loading-state,
        .card-content,
        .table-container,
        .no-data,
        .ant-table-wrapper,
        .ant-table-body,
        .ant-table-content,
        .ant-empty {
          background: transparent !important;
          color: #b8cdd9 !important;
        }

        .empty-icon,
        .ant-empty-image {
          color: #506878 !important;
          opacity: 0.6;
        }

        /* ===== Dashboard — Home page panels ===== */
        .dashboard-section {
          background: ${d.bgSecondary} !important;
          border-color: #1e3040 !important;
        }

        .dashboard-agent-item,
        .chat-item,
        .chat-history-card {
          background: ${d.bgTertiary} !important;
          border-color: #1e3040 !important;
          color: #eaf2f8 !important;
        }

        .dashboard-agent-item:hover,
        .chat-item:hover,
        .chat-history-card:hover {
          background: ${d.bgElevated} !important;
          border-color: #2a3e50 !important;
        }

        .action-card {
          background: ${d.bgSecondary} !important;
          border-color: #1e3040 !important;
        }

        .action-card:hover {
          background: ${d.bgTertiary} !important;
          border-color: ${brand.colors.primary} !important;
        }

        /* ===== Admin — Tenant Users & tables ===== */
        .card,
        .admin-card,
        .admin-panel,
        .admin-section,
        .tenant-users-card-fixed,
        .user-settings-card,
        .user-settings-section,
        .group-admin-card {
          background: ${d.bgSecondary} !important;
          border-color: #1e3040 !important;
          color: #eaf2f8 !important;
        }

        .card-header {
          border-bottom-color: #1e3040 !important;
        }

        /* ===== Insights ===== */
        .insight-modal-content,
        .insight-form-section,
        .insight-template-card,
        .reset-modal-content {
          background: ${d.bgSecondary} !important;
          color: #eaf2f8 !important;
          border-color: #1e3040 !important;
        }

        .insight-template-card:hover {
          background: ${d.bgTertiary} !important;
        }

        /* ===== Chatbots / Skills / Bundles ===== */
        .bot-form-panel,
        .bot-card,
        .bot-detail-panel,
        .skill-card,
        .skill-detail,
        .bundle-card,
        .bundle-detail,
        .bundle-view-content,
        .context-files-panel,
        .url-research-panel,
        .url-research-card {
          background: ${d.bgSecondary} !important;
          color: #eaf2f8 !important;
          border-color: #1e3040 !important;
        }

        /* ===== Login / Auth ===== */
        .login-form-panel,
        .form-container,
        .change-password-content {
          background: ${d.bgSecondary} !important;
          color: #eaf2f8 !important;
        }

        /* ===== Other panels ===== */
        .config-guide-content,
        .confirm-modal-content,
        .file-preview-content,
        .upload-area,
        .legal-card,
        .tenant-editor-section,
        .mobile-sidebar,
        .mobile-header {
          background: ${d.bgSecondary} !important;
          color: #eaf2f8 !important;
        }

        /* ===== Inputs inside modals/drawers ===== */
        .ant-modal input,
        .ant-modal textarea,
        .ant-modal .ant-select-selector,
        .ant-drawer input,
        .ant-drawer textarea,
        .ant-drawer .ant-select-selector {
          background: ${d.bgTertiary} !important;
          border-color: #1e3040 !important;
          color: #eaf2f8 !important;
        }

        /* ===== Misc ===== */
        input::placeholder,
        textarea::placeholder {
          color: #506878 !important;
        }

        ::-webkit-scrollbar-track {
          background: ${d.bgPrimary};
        }
        ::-webkit-scrollbar-thumb {
          background: #1e3040;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: #2a3e50;
        }
      }
    `;
    document.querySelector('style[data-brand="dark-mode-fixes"]')?.remove();
    document.head.appendChild(darkFix);
  }

  // Avatar brand colors — applies to BOTH light and dark modes
  const avatarStyle = document.createElement('style');
  avatarStyle.setAttribute('data-brand', 'avatar-colors');
  avatarStyle.textContent = `
    /* Default: M² primary blue */
    .bot-avatar,
    .bot-item-avatar,
    .chat-icon-wrapper,
    .group-bot-avatar,
    .chat-header-bot-avatar,
    .search-result-bot-avatar {
      background: linear-gradient(135deg, ${brand.colors.primary} 0%, ${brand.colors.primaryHover} 100%) !important;
    }

    /* 2nd: amber */
    .dashboard-agent-item:nth-child(4n+2) .bot-avatar,
    .chat-history-card:nth-child(4n+2) .chat-icon-wrapper,
    .group-item:nth-child(4n+2) .group-bot-avatar,
    .bot-item:nth-child(4n+2) .bot-item-avatar {
      background: linear-gradient(135deg, ${brand.colors.secondary} 0%, ${brand.colors.secondaryDark} 100%) !important;
    }

    /* 3rd: lime green */
    .dashboard-agent-item:nth-child(4n+3) .bot-avatar,
    .chat-history-card:nth-child(4n+3) .chat-icon-wrapper,
    .group-item:nth-child(4n+3) .group-bot-avatar,
    .bot-item:nth-child(4n+3) .bot-item-avatar {
      background: linear-gradient(135deg, ${brand.colors.accent} 0%, #9fb800 100%) !important;
    }

    /* 4th: red */
    .dashboard-agent-item:nth-child(4n) .bot-avatar,
    .chat-history-card:nth-child(4n) .chat-icon-wrapper,
    .group-item:nth-child(4n) .group-bot-avatar,
    .bot-item:nth-child(4n) .bot-item-avatar {
      background: linear-gradient(135deg, #D50032 0%, #b8002a 100%) !important;
    }

    /* General AI — muted */
    .group-bot-avatar-general,
    .chat-header-bot-avatar-general {
      background: linear-gradient(135deg, #7a95a8 0%, #506878 100%) !important;
    }

    /* Bot card hover — use brand primary instead of hardcoded blue */
    .bot-item:hover {
      border-color: ${hexToRgba(brand.colors.primary, 0.5)} !important;
      box-shadow: 0 4px 12px ${hexToRgba(brand.colors.primary, 0.15)} !important;
    }

    .action-card:hover {
      border-color: ${brand.colors.primary} !important;
    }
  `;
  document.querySelector('style[data-brand="avatar-colors"]')?.remove();
  document.head.appendChild(avatarStyle);

  // Sidebar spacing — more breathing room for buttons, icons, and chat items
  const sidebarStyle = document.createElement('style');
  sidebarStyle.setAttribute('data-brand', 'sidebar-spacing');
  sidebarStyle.textContent = `
    /* Nav items — more padding and spacing */
    .nav-menu {
      padding: 10px 8px !important;
    }

    .nav-item {
      padding: 10px 14px !important;
      margin-bottom: 3px !important;
      gap: 12px !important;
    }

    /* Sidebar header — more room */
    .sidebar-header {
      padding: 14px 14px 14px 18px !important;
      min-height: 56px !important;
    }

    /* Conversation items — roomier */
    .sidebar .conversation-item {
      padding: 11px 14px !important;
      margin-bottom: 2px !important;
      gap: 6px !important;
    }

    /* Group headers */
    .group-header {
      padding: 10px 14px !important;
      margin-bottom: 2px !important;
    }

    /* Conversations & bots sections */
    .conversations-section,
    .bots-section {
      padding: 6px 8px !important;
    }

    .conversations-title,
    .bots-title {
      padding: 8px 10px !important;
      margin-bottom: 4px !important;
    }

    /* Footer — more room */
    .sidebar-footer {
      padding: 12px 14px !important;
    }

    .sidebar-footer .user-info-wrapper {
      padding: 8px 10px !important;
      gap: 10px !important;
    }
  `;
  document.querySelector('style[data-brand="sidebar-spacing"]')?.remove();
  document.head.appendChild(sidebarStyle);

  // Page header consistency — all pages get same sizing, alignment, and separator
  const headerStyle = document.createElement('style');
  headerStyle.setAttribute('data-brand', 'page-headers');
  headerStyle.textContent = `
    /* ===== User avatars — circle, black bg, white text (dark mode) ===== */
    .user-avatar,
    .user-avatar-small,
    .user-avatar-large {
      border-radius: 50% !important;
      background: #111 !important;
      color: #fff !important;
      box-shadow: none !important;
    }

    /* Chat user avatars — black circle, same as admin user avatars */
    .message-group.user .message-avatar {
      background: #111 !important;
      color: #fff !important;
    }

    /* ===== No strokes in dark mode — use subtle bg difference instead ===== */
    .card,
    .bot-item,
    .action-card,
    .dashboard-section,
    .dashboard-agent-item,
    .chat-item,
    .chat-history-card,
    .insight-template-card,
    .skill-card,
    .bundle-card,
    .tree-node-content,
    .ant-card {
      border: none !important;
    }

    /* ===== Unified page headers — same layout everywhere ===== */
    /* Base: title left-aligned, subtitle below, bottom border separator */
    .simple-chat-header {
      text-align: start !important;
      padding-bottom: 16px !important;
      margin-bottom: 24px !important;
      border-bottom: 1px solid var(--border) !important;
    }

    /* When header has actions (buttons) on the right */
    .simple-chat-header.page-header-with-actions {
      display: flex !important;
      align-items: flex-start !important;
      justify-content: space-between !important;
    }

    /* Page title — 1.75rem Futura Heavy (700) so titles read substantial, not thin */
    .bot-title {
      font-size: 1.75rem !important;
      font-weight: 700 !important;
      margin: 0 0 4px 0 !important;
      letter-spacing: -0.01em !important;
    }

    /* Page subtitle */
    .text-muted {
      font-size: 0.875rem !important;
      margin: 0 !important;
    }

    /* Agents page — match Admin/Insights layout. Desktop only: on a phone
       the page keeps its own padding (BotsPage.css), whose top row leaves
       room for the floating menu button and the notification bell — this
       rule used to erase it, putting the first tab under the menu button
       and the last under the bell. */
    @media (min-width: 769px) {
      .bots-content {
        padding: var(--space-4) var(--space-6) !important;
        max-width: 100% !important;
        margin: 0 !important;
      }
    }

    .bots-section-header {
      border-bottom: none !important;
      border-top: none !important;
      padding-bottom: 4px !important;
    }

    .bots-section {
      border-top: none !important;
    }

    /* Home page — match background to all other pages */
    .dashboard-layout {
      background: var(--bg-primary) !important;
    }

    /* ===== No strokes / borders on cards globally ===== */
    .card,
    .bot-item,
    .action-card,
    .dashboard-section,
    .dashboard-agent-item,
    .chat-item,
    .chat-history-card,
    .insight-template-card,
    .skill-card,
    .bundle-card,
    .admin-card,
    .tenant-users-card-fixed,
    .tree-node-content,
    .ant-card,
    .ant-table,
    .ant-table-container,
    .ant-modal-content,
    .ant-drawer-content,
    .ant-input,
    .ant-input-affix-wrapper,
    .ant-select-selector,
    .bots-search-box,
    .bots-filter-select,
    .agent-tile div {
      border-color: transparent !important;
    }

    /* Light mode cards — white bg, no stroke, subtle shadow instead */
    [data-theme="light"] .card,
    [data-theme="light"] .bot-item,
    [data-theme="light"] .action-card,
    [data-theme="light"] .dashboard-section,
    [data-theme="light"] .dashboard-agent-item,
    [data-theme="light"] .chat-item,
    [data-theme="light"] .chat-history-card,
    [data-theme="light"] .insight-template-card,
    [data-theme="light"] .ant-card {
      background: #ffffff !important;
      border: none !important;
      box-shadow: 0 1px 3px rgba(15, 42, 61, 0.06) !important;
    }

    [data-theme="light"] .chat-item:hover,
    [data-theme="light"] .dashboard-agent-item:hover,
    [data-theme="light"] .bot-item:hover,
    [data-theme="light"] .action-card:hover {
      box-shadow: 0 4px 12px rgba(15, 42, 61, 0.1) !important;
    }

    /* Light mode user avatars — also black with white text */
    [data-theme="light"] .user-avatar,
    [data-theme="light"] .user-avatar-small,
    [data-theme="light"] .user-avatar-large {
      background: #111 !important;
      color: #fff !important;
      border: none !important;
    }

    /* Agents page — more space between controls and cards */
    .bots-controls {
      margin-bottom: 24px !important;
    }

    .bots-section {
      margin-top: 8px !important;
    }

    /* Chat page header — align to start, not centered */
    .chat-header-title-container {
      align-items: flex-start !important;
    }
  `;
  document.querySelector('style[data-brand="page-headers"]')?.remove();
  document.head.appendChild(headerStyle);

  // Backwards compatibility aliases
  root.style.setProperty('--claude-bg-primary', 'var(--bg-primary)');
  root.style.setProperty('--claude-bg-secondary', 'var(--bg-secondary)');
  root.style.setProperty('--claude-bg-tertiary', 'var(--bg-tertiary)');
  root.style.setProperty('--claude-text-primary', 'var(--text-primary)');
  root.style.setProperty('--claude-text-secondary', 'var(--text-secondary)');
  root.style.setProperty('--claude-text-muted', 'var(--text-muted)');
  root.style.setProperty('--claude-border', 'var(--border)');
  root.style.setProperty('--claude-accent', brand.colors.primary);
  root.style.setProperty('--claude-accent-hover', brand.colors.primaryHover);

  // Load custom fonts if specified. Replace (not skip-if-present) so a
  // brand swap actually fetches the new family — otherwise the page keeps
  // M²'s googleFontsUrl after switching to brij and brij's
  // Plus Jakarta Sans / Arbutus Slab never load, falling back to system
  // sans which visually matches M²'s Manrope.
  if (brand.fonts?.googleFontsUrl) {
    const existing = document.querySelector('link[data-brand="fonts"]') as HTMLLinkElement | null;
    if (existing) {
      if (existing.href !== brand.fonts.googleFontsUrl) {
        existing.href = brand.fonts.googleFontsUrl;
      }
    } else {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = brand.fonts.googleFontsUrl;
      link.setAttribute('data-brand', 'fonts');
      document.head.appendChild(link);
    }

    root.style.setProperty('--font-heading', brand.fonts.heading);
    root.style.setProperty('--font-body', brand.fonts.body);
    root.style.setProperty('--font-mono', brand.fonts.mono);

    // Override font-family on body and all elements via a style tag
    // EN: Futura (headings), Manrope (body)
    // HE: Ploni ML v2 AAA Bold (headings), Ploni ML v2 AAA Regular (body)
    // Fallbacks: Jost → Futura, Varela Round → Ploni
    const fontStyle = document.createElement('style');
    fontStyle.setAttribute('data-brand', 'fonts-override');
    fontStyle.textContent = `
      /* Body text — Manrope (EN), Ploni Regular (HE) */
      body, html, input, textarea, select, button, p, span, div, li, td, th, label, a {
        font-family: '${brand.fonts.body}', 'Ploni ML v2 AAA', 'Varela Round', -apple-system, BlinkMacSystemFont, sans-serif !important;
      }

      /* Headings — Futura (EN), Ploni Bold (HE) */
      h1, h2, h3, h4, h5, h6,
      .greeting-text,
      .section-title,
      .branding-tagline,
      .mobile-header-title,
      .modal-title,
      .card-title,
      .bot-name,
      .chat-title,
      .ant-modal-title,
      .ant-card-head-title,
      .ant-drawer-title,
      .ant-collapse-header-text,
      .ant-list-item-meta-title,
      .tab-btn,
      /* Agent chat message body — Futura applied to the container and the
         specific markdown tags we actually render. Explicit tag list instead
         of a wildcard so future custom children don't silently inherit Futura
         and lose their own fonts (e.g. monospace, icon fonts). */
      .message-group.assistant .message-text,
      .message-group.assistant .message-text p,
      .message-group.assistant .message-text h1,
      .message-group.assistant .message-text h2,
      .message-group.assistant .message-text h3,
      .message-group.assistant .message-text h4,
      .message-group.assistant .message-text h5,
      .message-group.assistant .message-text h6,
      .message-group.assistant .message-text ul,
      .message-group.assistant .message-text ol,
      .message-group.assistant .message-text li,
      .message-group.assistant .message-text blockquote,
      .message-group.assistant .message-text em,
      .message-group.assistant .message-text strong,
      .message-group.assistant .message-text a,
      .agent-intro-text,
      .welcome-title,
      /* The "How can I help you today?" line carries welcome-title-line TOO
         (see MainPage.tsx), so it must be excluded here or this rule wins
         over the Manrope declared for it in MainPage.css. */
      .welcome-title-line:not(.welcome-title-question),
      /* Concierge empty-state greeting — <p> tags, so without this they'd be
         caught by the body rule above and lose the display face. */
      .concierge-empty-title,
      .concierge-empty-sub,
      .agent-picker-title,
      .chat-header-bot-name,
      .branch-switcher,
      .branch-switcher-seg,
      /* Home agent picker — names + descriptions on desktop tiles & mobile carousel */
      .agent-tile-name,
      .agent-tile-desc,
      .carousel-item-name,
      .carousel-desc,
      .carousel-desc p,
      .selected-agent-label {
        font-family: '${brand.fonts.heading}', 'Jost', 'Ploni ML v2 AAA', 'Varela Round', -apple-system, BlinkMacSystemFont, sans-serif !important;
      }

      /* Code inside agent messages stays monospace */
      .message-group.assistant .message-text code,
      .message-group.assistant .message-text pre,
      .message-group.assistant .message-text pre code {
        font-family: '${brand.fonts.mono}', 'SF Mono', 'Fira Code', 'Consolas', monospace !important;
      }

      /* Hebrew override — when dir=rtl, Ploni takes priority */
      [dir="rtl"] body, [dir="rtl"] html, [dir="rtl"] input, [dir="rtl"] textarea, [dir="rtl"] select, [dir="rtl"] button, [dir="rtl"] p, [dir="rtl"] span, [dir="rtl"] div {
        font-family: 'Ploni ML v2 AAA', 'Varela Round', '${brand.fonts.body}', -apple-system, BlinkMacSystemFont, sans-serif !important;
      }

      [dir="rtl"] h1, [dir="rtl"] h2, [dir="rtl"] h3, [dir="rtl"] h4, [dir="rtl"] h5, [dir="rtl"] h6,
      [dir="rtl"] .greeting-text, [dir="rtl"] .section-title, [dir="rtl"] .branding-tagline,
      [dir="rtl"] .modal-title, [dir="rtl"] .ant-modal-title, [dir="rtl"] .ant-card-head-title,
      [dir="rtl"] .ant-drawer-title, [dir="rtl"] .ant-collapse-header-text, [dir="rtl"] .ant-list-item-meta-title,
      [dir="rtl"] .message-group.assistant .message-text,
      [dir="rtl"] .message-group.assistant .message-text p,
      [dir="rtl"] .message-group.assistant .message-text h1,
      [dir="rtl"] .message-group.assistant .message-text h2,
      [dir="rtl"] .message-group.assistant .message-text h3,
      [dir="rtl"] .message-group.assistant .message-text h4,
      [dir="rtl"] .message-group.assistant .message-text h5,
      [dir="rtl"] .message-group.assistant .message-text h6,
      [dir="rtl"] .message-group.assistant .message-text ul,
      [dir="rtl"] .message-group.assistant .message-text ol,
      [dir="rtl"] .message-group.assistant .message-text li,
      [dir="rtl"] .message-group.assistant .message-text blockquote,
      [dir="rtl"] .message-group.assistant .message-text em,
      [dir="rtl"] .message-group.assistant .message-text strong,
      [dir="rtl"] .message-group.assistant .message-text a,
      [dir="rtl"] .chat-header-bot-name {
        font-family: 'Ploni ML v2 AAA', 'Varela Round', '${brand.fonts.heading}', -apple-system, BlinkMacSystemFont, sans-serif !important;
      }

      /* Code — always monospace */
      code, pre, .code-block {
        font-family: '${brand.fonts.mono}', 'SF Mono', 'Fira Code', 'Consolas', monospace !important;
      }
    `;
    document.querySelector('style[data-brand="fonts-override"]')?.remove();
    document.head.appendChild(fontStyle);
  }

  // The favicon was applied at the top of this function (applyBrandFavicon:
  // dedicated `favicon`, then the icon as a fallback). A second, older block
  // used to sit here and overwrite `link[rel="icon"]` with `icon` / `iconDark`
  // regardless of `favicon` — so a tenant's uploaded favicon showed in the
  // editor and never in the tab, and the sidebar icon "changed the favicon"
  // (seen 2026-09-27). One writer only.

  // Page title
  document.title = brand.pageTitle;
}
